/**
 * Quick order table: predictive product search, line items, and bulk add to cart.
 * @extends HTMLElement
 */
const QUICK_ORDER_PREDICTIVE_PRODUCT_FIELDS =
  'body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor';
/** Ms to wait after typing before calling `/search/suggest` (lower = snappier). */
const QUICK_ORDER_SUGGEST_DEBOUNCE_MS = 200;

class ComponentQuickOrder extends HTMLElement {
  constructor() {
    super();

    this.searchRowTemplate = this.querySelector('template[data-quick-order-search-row-template]');
    this.addNewLineButton = this.querySelector('[data-add-new-line]');
    this.sectionId = this.dataset.sectionId;
    this.addToCartButton = null;

    this.boundHideSuggestPanelsOnScroll = this.hideAllSuggestPanels.bind(this);
    this.boundOnDocumentDelegatedQtyInput = this.onDocumentDelegatedQtyInput.bind(this);
    this.boundOnComponentInput = this.onComponentInput.bind(this);
    this.boundOnComponentClick = this.onComponentClick.bind(this);
    this.boundOnPredictiveKeydown = this.onPredictiveKeydown.bind(this);
    this.boundOnComponentChange = this.onComponentChange.bind(this);
    this.boundOnBulkAddToCart = this.onBulkAddToCart.bind(this);
    this.boundOnAddNewLineClick = this.onAddNewLineClick.bind(this);
  }

  /**
   * Lifecycle callback when element is connected to DOM
   */
  connectedCallback() {
    this.bindEvents();
    this.initializeDefaultTableState();
  }

  /**
   * Lifecycle callback when element is disconnected from DOM
   */
  disconnectedCallback() {
    this.unbindEvents();
  }

  /**
   * Registers event listeners (same lifecycle pattern as `bindEvents` on `CustomSelect` in `custom-components.js`).
   */
  bindEvents() {
    this.addNewLineButton?.addEventListener('click', this.boundOnAddNewLineClick);
    window.addEventListener('scroll', this.boundHideSuggestPanelsOnScroll);

    this.addEventListener('click', this.boundOnComponentClick);
    this.addEventListener('keydown', this.boundOnPredictiveKeydown);
    this.addEventListener('change', this.boundOnComponentChange);
    this.addEventListener('input', this.boundOnComponentInput);

    document.addEventListener('change', this.boundOnDocumentDelegatedQtyInput);
    document.addEventListener('input', this.boundOnDocumentDelegatedQtyInput);

    this.addToCartButton = this.getQuickOrderShell()?.querySelector('[data-add-to-cart]') ?? null;
    this.addToCartButton?.addEventListener('click', this.boundOnBulkAddToCart);
  }

  /**
   * Removes all listeners registered in `bindEvents`.
   */
  unbindEvents() {
    this.addNewLineButton?.removeEventListener('click', this.boundOnAddNewLineClick);
    window.removeEventListener('scroll', this.boundHideSuggestPanelsOnScroll);

    this.removeEventListener('click', this.boundOnComponentClick);
    this.removeEventListener('keydown', this.boundOnPredictiveKeydown);
    this.removeEventListener('change', this.boundOnComponentChange);
    this.removeEventListener('input', this.boundOnComponentInput);

    document.removeEventListener('change', this.boundOnDocumentDelegatedQtyInput);
    document.removeEventListener('input', this.boundOnDocumentDelegatedQtyInput);

    this.addToCartButton?.removeEventListener('click', this.boundOnBulkAddToCart);
    this.addToCartButton = null;

    this.querySelectorAll('tr[data-search-row]').forEach((searchRow) => {
      const searchInput = searchRow.querySelector('[data-search-input]');
      const debouncedSuggest = searchRow._qoDebouncedSuggest;
      if (searchInput && debouncedSuggest) {
        searchInput.removeEventListener('input', debouncedSuggest);
        searchInput.removeEventListener('search', debouncedSuggest);
      }
      delete searchRow._qoDebouncedSuggest;
    });
  }

  /**
   * First paint: one search line, table chrome, totals.
   */
  initializeDefaultTableState() {
    this.appendSearchLine();
    this.syncTableUi();
    this.recalculateOrderGrandTotal();
  }

  /** @param {MouseEvent} evt */
  onAddNewLineClick(evt) {
    evt.preventDefault();
    this.appendSearchLine({ focus: true });
  }

  /** @returns {HTMLElement|null} */
  getQuickOrderShell() {
    return this.closest('.quick-order-component');
  }

  /** @returns {HTMLTableSectionElement|null} */
  getOrderTableBody() {
    return (
      this.querySelector('[data-quick-order-tbody]') ||
      this.querySelector('.quick-order-table-wrapper tbody')
    );
  }

  /**
   * Resolves `[data-search-results]` markup from a `/search/suggest` HTML response.
   * Prefer `[data-quick-order-suggest-liquid-source]` (SSR) because nodes inside `<template>` are not queryable.
   * @param {Document} parsedSuggestDocument
   * @param {string} sectionId
   * @returns {HTMLElement|null}
   */
  findSearchResultsHostInDoc(parsedSuggestDocument, sectionId) {
    const fromLiquidSource = parsedSuggestDocument.querySelector(
      '[data-quick-order-suggest-liquid-source] [data-search-results]'
    );
    if (fromLiquidSource) return fromLiquidSource;

    if (sectionId) {
      const sectionRoot = parsedSuggestDocument.getElementById(`shopify-section-${sectionId}`);
      const fromSection = sectionRoot?.querySelector('[data-search-results]') ?? null;
      if (fromSection) return fromSection;
    }

    let host = parsedSuggestDocument.querySelector('[data-search-results]');
    if (host) return host;

    const rowTemplate = parsedSuggestDocument.querySelector('template[data-quick-order-search-row-template]');
    if (rowTemplate?.content) {
      host = rowTemplate.content.querySelector('[data-search-results]');
      if (host) return host;
    }

    return null;
  }

  /**
   * @param {HTMLTableRowElement} searchRowClone
   */
  assignUniqueIdsToClonedSearchRow(searchRowClone) {
    const sectionSlug = String(this.sectionId || 'qo').replace(/[^\w-]/g, '');
    const lineUid = `${sectionSlug}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
    searchRowClone.querySelectorAll('[id]').forEach((element) => {
      if (element.id.includes('__QO_UID__')) {
        element.id = element.id.replaceAll('__QO_UID__', lineUid);
      }
    });
    searchRowClone.querySelectorAll('label[for]').forEach((label) => {
      const htmlFor = label.getAttribute('for');
      if (htmlFor?.includes('__QO_UID__')) {
        label.setAttribute('for', htmlFor.replaceAll('__QO_UID__', lineUid));
      }
    });
  }

  /**
   * @param {{ focus?: boolean }} [options]
   */
  appendSearchLine(options = {}) {
    const shouldFocusInput = options.focus === true;
    if (!this.searchRowTemplate?.content) return;

    const tableBody = this.getOrderTableBody();
    if (!tableBody) return;

    const fragment = this.searchRowTemplate.content.cloneNode(true);
    const searchRow = fragment.querySelector('tr[data-search-row]');
    if (!searchRow) return;

    this.assignUniqueIdsToClonedSearchRow(searchRow);
    tableBody.appendChild(searchRow);

    const searchInput = searchRow.querySelector('[data-search-input]');
    if (searchInput) {
      const debouncedSuggest = Utility.debounce(
        () => this.fetchSuggestForInput(searchInput),
        QUICK_ORDER_SUGGEST_DEBOUNCE_MS
      );
      searchRow._qoDebouncedSuggest = debouncedSuggest;
      searchInput.addEventListener('input', debouncedSuggest);
      searchInput.addEventListener('search', debouncedSuggest);
    }
    this.bindOverlayScrollbar(searchRow);
    if (shouldFocusInput && searchInput) searchInput.focus();
  }

  /**
   * Overlay scrollbar for [data-search-results] — visible but takes no layout width.
   * @param {HTMLTableRowElement} searchRow
   */
  bindOverlayScrollbar(searchRow) {
    const resultsContainer = searchRow.querySelector('[data-search-results]');
    if (!resultsContainer || resultsContainer._qoOverlayScrollBound) return;

    resultsContainer._qoOverlayScrollBound = true;
    resultsContainer.addEventListener('scroll', () => this.syncOverlayScrollbar(searchRow), {
      passive: true,
    });
  }

  /**
   * @param {HTMLTableRowElement|null|undefined} searchRow
   */
  syncOverlayScrollbar(searchRow) {
    if (!searchRow) return;

    const resultsContainer = searchRow.querySelector('[data-search-results]');
    const rail = searchRow.querySelector('[data-search-scroll-rail]');
    const thumb = searchRow.querySelector('[data-search-scroll-thumb]');
    if (!resultsContainer || !rail || !thumb) return;

    const { scrollHeight, clientHeight, scrollTop } = resultsContainer;
    if (scrollHeight <= clientHeight + 1) {
      rail.hidden = true;
      return;
    }

    rail.hidden = false;
    const railHeight = rail.clientHeight || clientHeight;
    const thumbHeight = Math.max((clientHeight / scrollHeight) * railHeight, 24);
    const maxThumbTop = Math.max(railHeight - thumbHeight, 0);
    const maxScrollTop = Math.max(scrollHeight - clientHeight, 1);
    const thumbTop = (scrollTop / maxScrollTop) * maxThumbTop;

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  }

  /**
   * @param {string} handle
   * @returns {string}
   */
  static productJsonUrl(handle) {
    const root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? String(Shopify.routes.root)
        : '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    return `${base}products/${encodeURIComponent(handle)}.js`;
  }

  /**
   * @param {string} handle
   * @param {string|number} [variantId]
   * @returns {string}
   */
  static productUrl(handle, variantId) {
    if (!handle) return '';
    const root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? String(Shopify.routes.root)
        : '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const url = `${base}products/${encodeURIComponent(handle)}`;
    return variantId ? `${url}?variant=${encodeURIComponent(String(variantId))}` : url;
  }

  /**
   * Theme placeholder-svg markup from `{% render 'placeholder-image' %}`.
   * @returns {string}
   */
  getPlaceholderImageHtml() {
    const template = this.querySelector('template[data-placeholder-image-template]');
    return template?.innerHTML?.trim() || '';
  }

  /**
   * @param {object} variant
   * @param {object} product
   * @returns {string}
   */
  static variantImageSrcFromProductJson(variant, product) {
    if (variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    if (variant.image_id && Array.isArray(product.images)) {
      const matched = product.images.find((image) => image.id === variant.image_id);
      if (matched && matched.src) return matched.src;
    }
    if (product.images && product.images[0] && product.images[0].src) {
      return product.images[0].src;
    }
    return '';
  }

  /**
   * Predictive search returns one variant per product; fetch full product JSON to list all variants.
   * @param {HTMLElement} resultsContainer
   * @param {number} requestSequence
   * @param {HTMLTableRowElement} searchRow
   * @param {AbortSignal|undefined} signal
   * @returns {Promise<void>}
   */
  async expandMultiVariantSuggestResults(resultsContainer, requestSequence, searchRow, signal) {
    const expandCards = Array.from(
      resultsContainer.querySelectorAll('[data-product-card][data-qo-expand-variants="true"]')
    );
    if (!expandCards.length) return;

    const seenHandles = new Set();
    const tasks = [];

    for (const card of expandCards) {
      const handle = card.getAttribute('data-product-handle');
      if (!handle || seenHandles.has(handle)) continue;
      seenHandles.add(handle);

      const variantsCount = parseInt(card.getAttribute('data-product-variants-count') || '0', 10);
      const existingCount = resultsContainer.querySelectorAll(
        `[data-product-card][data-product-handle="${handle}"]`
      ).length;
      if (variantsCount > 1 && existingCount >= variantsCount) continue;

      tasks.push(
        this.expandOneProductVariantsInSuggest(
          resultsContainer,
          card,
          handle,
          requestSequence,
          searchRow,
          signal
        )
      );
    }

    await Promise.all(tasks);
  }

  /**
   * @param {HTMLElement} resultsContainer
   * @param {HTMLElement} card
   * @param {string} handle
   * @param {number} requestSequence
   * @param {HTMLTableRowElement} searchRow
   * @param {AbortSignal|undefined} signal
   * @returns {Promise<void>}
   */
  async expandOneProductVariantsInSuggest(
    resultsContainer,
    card,
    handle,
    requestSequence,
    searchRow,
    signal
  ) {
    if (requestSequence !== searchRow._qoSuggestSeq) return;

    try {
      const response = await fetch(ComponentQuickOrder.productJsonUrl(handle), { signal });
      if (!response.ok || requestSequence !== searchRow._qoSuggestSeq) return;

      const product = await response.json();
      if (requestSequence !== searchRow._qoSuggestSeq) return;

      const variants = (product.variants || []).filter((variant) => variant.available !== false);
      if (variants.length <= 1) return;

      if (!resultsContainer.contains(card)) return;
      const parent = card.parentElement;
      if (!parent) return;

      const fragment = document.createDocumentFragment();
      variants.forEach((variant) => {
        fragment.appendChild(this.buildSuggestVariantCardFromTemplate(card, variant, product));
      });
      parent.insertBefore(fragment, card);
      card.remove();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Quick order variant expand:', error);
    }
  }

  /**
   * @param {HTMLElement} templateCard
   * @param {object} variant
   * @param {object} product
   * @returns {HTMLElement}
   */
  buildSuggestVariantCardFromTemplate(templateCard, variant, product) {
    const li = /** @type {HTMLElement} */ (templateCard.cloneNode(true));
    li.removeAttribute('data-qo-expand-variants');

    const productTitle = templateCard.getAttribute('data-product-title') || product.title || '';
    li.setAttribute('data-product-title', productTitle);

    const handle = product.handle || templateCard.getAttribute('data-product-handle') || '';
    if (handle) {
      li.setAttribute('data-product-handle', handle);
      li.setAttribute('data-product-url', ComponentQuickOrder.productUrl(handle, variant.id));
    }
    li.setAttribute('data-variant-sku', variant.sku || '');

    const consultantMap = ComponentQuickOrder.parseVariantConsultantMap(templateCard);
    const consultantValues = consultantMap[String(variant.id)] || {};
    const points = String(consultantValues.points || '').trim();
    const bonusUs = String(consultantValues.bonusUs || '').trim();
    const bonusCa = String(consultantValues.bonusCa || '').trim();
    li.setAttribute('data-consultant-points', points);
    li.setAttribute('data-consultant-bonus-us', bonusUs);
    li.setAttribute('data-consultant-bonus-ca', bonusCa);
    li.setAttribute('data-consultant-bonus', bonusUs);

    const variantImage = ComponentQuickOrder.variantImageSrcFromProductJson(variant, product);
    const thumbWrap = li.querySelector('.predictive-search__thumb');

    if (variantImage) {
      li.setAttribute('data-product-featured-image', variantImage);
      let thumb = li.querySelector('.predictive-search__thumb-img');
      if (thumb instanceof HTMLImageElement) {
        thumb.src = variantImage;
      } else if (thumbWrap) {
        thumbWrap.innerHTML = '';
        thumb = document.createElement('img');
        thumb.src = variantImage;
        thumb.alt = '';
        thumb.width = 110;
        thumb.height = 110;
        thumb.className = 'predictive-search__thumb-img rounded object-fit-contain';
        thumbWrap.appendChild(thumb);
      }
    } else {
      li.removeAttribute('data-product-featured-image');
      if (thumbWrap) {
        const placeholderHtml = this.getPlaceholderImageHtml();
        thumbWrap.innerHTML = placeholderHtml || '';
      }
    }

    const script = li.querySelector('script[type="application/json"]:not([data-qo-variant-consultant-map])');
    if (script) {
      script.textContent = JSON.stringify({ ...variant, product_title: product.title });
    }

    const variantLabel = variant.title && variant.title !== 'Default Title' ? variant.title : '';
    const body = li.querySelector('.predictive-search__row-body');
    if (body) {
      const titleEl = body.querySelector('.predictive-search__row-title');
      if (titleEl) titleEl.textContent = productTitle;

      let metaRow = body.querySelector('.d-flex.flex-wrap');
      if (!variantLabel && !variant.sku) {
        metaRow?.remove();
      } else {
        if (!metaRow) {
          metaRow = document.createElement('div');
          metaRow.className = 'd-flex flex-wrap align-items-baseline gap-2';
          body.appendChild(metaRow);
        }
        metaRow.innerHTML = '';
        if (variantLabel) {
          const labelSpan = document.createElement('span');
          labelSpan.className = 'cart-drawer-item__meta fs-sm text-gray-700 font-family-base';
          labelSpan.textContent = variantLabel;
          metaRow.appendChild(labelSpan);
        }
        if (variant.sku) {
          const skuSpan = document.createElement('span');
          skuSpan.className =
            'cart-drawer-item__meta fs-sm text-gray-700 font-family-base text-nowrap';
          skuSpan.textContent = `SKU: ${variant.sku}`;
          metaRow.appendChild(skuSpan);
        }
      }
    }

    return li;
  }

  /**
   * @param {HTMLElement} card
   * @returns {Record<string, { points?: string, bonusUs?: string, bonusCa?: string }>}
   */
  static parseVariantConsultantMap(card) {
    const mapScript = card.querySelector('script[data-qo-variant-consultant-map]');
    if (!mapScript?.textContent) return {};
    try {
      const parsed = JSON.parse(mapScript.textContent);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * Match strength of a card's SKU against the typed query (lower sorts first).
   * @param {HTMLElement} card
   * @param {string} normalizedQuery
   * @returns {number}
   */
  static skuMatchRank(card, normalizedQuery) {
    const sku = String(card.getAttribute('data-variant-sku') || '')
      .trim()
      .toLowerCase();
    if (!sku) return 3;
    if (sku === normalizedQuery) return 0;
    if (sku.startsWith(normalizedQuery)) return 1;
    if (sku.includes(normalizedQuery)) return 2;
    return 3;
  }

  /**
   * Puts SKU matches at the top of the suggest list so a typed SKU is not buried
   * under the product's other sizes. Non-matching rows keep their original order.
   * @param {HTMLElement} resultsContainer
   * @param {string} query
   */
  reorderSuggestResultsBySkuMatch(resultsContainer, query) {
    const normalizedQuery = String(query || '').trim().toLowerCase();
    if (!normalizedQuery) return;

    resultsContainer.querySelectorAll('ul.results-list').forEach((list) => {
      const cards = Array.from(list.querySelectorAll(':scope > [data-product-card]'));
      if (cards.length < 2) return;

      const ranked = cards.map((card, index) => ({
        card,
        index,
        rank: ComponentQuickOrder.skuMatchRank(card, normalizedQuery),
      }));
      if (ranked.every((entry) => entry.rank === ranked[0].rank)) return;

      ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
      const fragment = document.createDocumentFragment();
      ranked.forEach((entry) => fragment.appendChild(entry.card));
      list.appendChild(fragment);
    });
  }

  /**
   * @param {HTMLInputElement} searchInput
   */
  fetchSuggestForInput(searchInput) {
    const searchRow = searchInput?.closest('tr[data-search-row]');
    const resultsContainer = searchRow?.querySelector('[data-search-results]');
    const predictiveShell = searchRow?.querySelector('[data-quick-order-predictive]');
    if (!searchInput || !searchRow || !resultsContainer || !predictiveShell) return;

    const query = searchInput.value.trim();
    if (!query.length) {
      searchRow._qoSuggestAbort?.abort();
      searchRow._qoSuggestSeq = (searchRow._qoSuggestSeq || 0) + 1;
      this.clearSuggestUiForSearchRow(searchRow, { clearInput: false });
      return;
    }

    searchRow._qoSuggestAbort?.abort();
    searchRow._qoSuggestAbort = new AbortController();
    const { signal } = searchRow._qoSuggestAbort;
    searchRow._qoSuggestSeq = (searchRow._qoSuggestSeq || 0) + 1;
    const requestSequence = searchRow._qoSuggestSeq;

    const sectionId = this.sectionId || '';
    const fieldsParam = encodeURIComponent(QUICK_ORDER_PREDICTIVE_PRODUCT_FIELDS);
    const suggestUrl = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide&resources[options][fields]=${fieldsParam}&section_id=${encodeURIComponent(sectionId)}`;

    fetch(suggestUrl, { signal })
      .then((response) => response.text())
      .then(async (htmlText) => {
        if (requestSequence !== searchRow._qoSuggestSeq) return;
        if (!searchInput.isConnected || !searchRow.contains(searchInput)) return;

        const parsed = new DOMParser().parseFromString(htmlText, 'text/html');
        const resultsHost = this.findSearchResultsHostInDoc(parsed, sectionId);
        if (!resultsHost || requestSequence !== searchRow._qoSuggestSeq) return;

        resultsContainer.innerHTML = resultsHost.innerHTML;
        resultsContainer.classList.remove('d-none');
        predictiveShell.classList.add('show-results');
        this.reorderSuggestResultsBySkuMatch(resultsContainer, query);
        requestAnimationFrame(() => this.syncOverlayScrollbar(searchRow));

        this.expandMultiVariantSuggestResults(
          resultsContainer,
          requestSequence,
          searchRow,
          signal
        )
          .then(() => {
            if (requestSequence !== searchRow._qoSuggestSeq) return;
            this.reorderSuggestResultsBySkuMatch(resultsContainer, query);
            this.syncOverlayScrollbar(searchRow);
          })
          .catch((error) => {
            if (error?.name === 'AbortError') return;
            console.error('Quick order variant expand:', error);
          });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('Quick order suggest:', error);
      });
  }

  /** @param {KeyboardEvent} evt */
  onPredictiveKeydown(evt) {
    if (evt.key !== 'Enter' && evt.key !== ' ') return;
    if (!this.contains(evt.target)) return;
    if (evt.target.closest('a.predictive-search__view-all-btn')) return;

    const productCard = evt.target.closest('[data-product-card]');
    const searchRow = productCard?.closest('tr[data-search-row]');
    if (!productCard || !searchRow) return;

    evt.preventDefault();
    this.addOrderRowFromProductCard(productCard);
  }

  /**
   * @param {HTMLElement} productCard
   */
  addOrderRowFromProductCard(productCard) {
    const variantScript = productCard.querySelector('script[type="application/json"]');
    if (!variantScript) return;

    let variant;
    try {
      variant = JSON.parse(variantScript.textContent);
    } catch {
      return;
    }

    const searchRow = productCard.closest('tr[data-search-row]');
    if (!searchRow) return;

    const tableBody = searchRow.closest('tbody');
    if (!tableBody) return;

    const imageUrl = productCard.getAttribute('data-product-featured-image') || '';
    const productTitle =
      productCard.getAttribute('data-product-title')?.trim() || variant.product_title || '';
    const displayTitle = productTitle || variant.title?.trim() || '';
    const sku = variant.sku || '';
    const variantSubtitle =
      variant.title && variant.title !== 'Default Title' ? variant.title : '';
    const unitPriceLabel = (variant.price / 100).toFixed(2);
    const unitPriceCents = variant.price;
    const variantId = variant.id;
    const productHandle = productCard.getAttribute('data-product-handle') || '';
    const productUrl =
      productCard.getAttribute('data-product-url') ||
      ComponentQuickOrder.productUrl(productHandle, variantId);

    if (tableBody.querySelector(`[data-quick-order-row][data-variant-id="${variantId}"]`)) {
      this.clearSuggestUiForSearchRow(searchRow);
      return;
    }

    const quantityMin =
      variant.quantity_rule && variant.quantity_rule.min ? variant.quantity_rule.min : 1;
    const quantityMax =
      variant.quantity_rule && variant.quantity_rule.max ? variant.quantity_rule.max : '';
    const quantityStep =
      variant.quantity_rule && variant.quantity_rule.increment ? variant.quantity_rule.increment : 1;
    const quantityMaxAttribute = quantityMax ? `max="${quantityMax}"` : '';

    // UI only for consultant-tagged customers; ATC still attaches Points/Bonus via localStorage gate.
    const showConsultantPointsLine = this.dataset.consultantPointsLine === 'true';
    const pointsBonusRowHtml = showConsultantPointsLine
      ? `<p data-points-and-bonus class="cart-drawer-item__meta fs-sm text-gray-700 font-family-base mb-0 d-flex flex-wrap align-items-baseline gap-2">Points: <span data-qo-points-value></span> <span>/</span> Bonus: <span data-qo-bonus-value></span></p>`
      : '';

    const productImageHtml = imageUrl
      ? `<img src="${imageUrl}" alt="product image" width="77" height="77" class="object-fit-contain rounded">`
      : this.getPlaceholderImageHtml();

    const orderRow = document.createElement('tr');
    orderRow.setAttribute('data-quick-order-row', '');
    orderRow.setAttribute('data-variant-id', variantId);
    orderRow.setAttribute('data-product-id', variant.product_id || '');

    const consultantPoints = productCard.getAttribute('data-consultant-points') || '';
    const consultantBonusUs = productCard.getAttribute('data-consultant-bonus-us') || '';
    const consultantBonusCa = productCard.getAttribute('data-consultant-bonus-ca') || '';
    const consultantBonus =
      productCard.getAttribute('data-consultant-bonus') || consultantBonusUs;
    orderRow.setAttribute('data-consultant-points', consultantPoints);
    orderRow.setAttribute('data-consultant-bonus-us', consultantBonusUs);
    orderRow.setAttribute('data-consultant-bonus-ca', consultantBonusCa);
    orderRow.setAttribute('data-consultant-bonus', consultantBonus);

    orderRow.innerHTML = `
          <td class="align-middle">
            <div class="d-flex gap-lg-5 gap-3 pd-and-info align-items-center">
              <div class='product-img flex-shrink-0'>${productImageHtml}</div>
              <div class='w-100'>
                <a href="#" class="h6 mb-2 pr-title text-black" data-product-title></a>
                <div class="d-flex flex-column gap-1">
                  <div class="d-flex flex-wrap align-items-baseline gap-2">
                  <div class="cart-drawer-item__meta fs-sm text-gray-700 font-family-base ravi" data-product-variant-line></div>
                  <div class="cart-drawer-item__meta fs-sm text-gray-700 font-family-base" data-product-sku-line></div>
                  </div>
                  ${pointsBonusRowHtml}
                </div>
              </div>
            </div>
          </td>
          <td class="align-middle fw-bold">$${unitPriceLabel}</td>
          <td class="align-middle">
            <quantity-input>
              <div class="input-group border text-black">
                <a href="#" class="border-0 bg-transparent align-items-center d-flex decrease bty-btn text-black" data-for="decrease" data-qty-btn>
                <span class="btn-decrease d-flex align-items-center justify-content-center">
                    <svg aria-hidden="true" focusable="false" style="width: 10px; height: 8px;">
                        <use href="#icon-minus"></use>
                    </svg>
                </span>
                </a>
                <input type="text" class="quantity form-control px-2 border-transparent text-center" data-qty-input value="${quantityMin}" min="${quantityMin}" ${quantityMaxAttribute} step="${quantityStep}" >
                <a href="#" class="border-0 bg-transparent align-items-center d-flex increase text-black" data-for="increase" data-qty-btn>
                <span class="btn-increase d-flex align-items-center justify-content-center">
                    <svg aria-hidden="true" focusable="false" style="width: 10px; height: 10px;">
                        <use href="#icon-quick-plus"></use>
                    </svg>
                </span>
                </a>
              </div>
            </quantity-input>
          </td>
          <td class="align-middle">
            <div class="align-items-center d-inline-flex flex-nowrap gap-2 justify-content-between w-100">
              <span data-row-total class="fw-bold">${Utility.formatMoney(unitPriceCents * quantityMin, globalThis.themeData?.money_format)}</span>
              <button type="button" class="quick-order-row__remove border-0 bg-transparent p-0 d-inline-flex align-items-center justify-content-center" data-remove-row>
                <svg aria-hidden="true" focusable="false" width="14" height="14" class="quick-order-row__remove-icon"><use href="#icon-close-xs"></use></svg>
              </button>
            </div>
          </td>
        `;

    tableBody.insertBefore(orderRow, searchRow);
    searchRow.remove();

    const titleElement = orderRow.querySelector('[data-product-title]');
    if (titleElement) {
      titleElement.textContent = displayTitle;
      if (productUrl) titleElement.setAttribute('href', productUrl);
    }
    const skuElement = orderRow.querySelector('[data-product-sku-line]');
    if (skuElement) skuElement.textContent = `SKU: ${sku}`;
    const variantLineElement = orderRow.querySelector('[data-product-variant-line]');
    if (variantLineElement) {
      variantLineElement.textContent = variantSubtitle;
      variantLineElement.classList.toggle('d-none', !variantSubtitle);
    }
    const pointsBonusEl = orderRow.querySelector('[data-points-and-bonus]');
    if (pointsBonusEl) {
      const pts = consultantPoints;
      const bns = Utility.resolveConsultantBonus({
        bonusUs: consultantBonusUs,
        bonusCa: consultantBonusCa,
        bonus: consultantBonus,
      });
      const ptsSpan = pointsBonusEl.querySelector('[data-qo-points-value]');
      const bnsSpan = pointsBonusEl.querySelector('[data-qo-bonus-value]');
      if (ptsSpan) ptsSpan.textContent = pts;
      if (bnsSpan) bnsSpan.textContent = bns;
      if (!pts && !bns) {
        pointsBonusEl.classList.add('d-none');
      }
    }
    const thumbImage = orderRow.querySelector('.product-img img');
    if (thumbImage && displayTitle) thumbImage.alt = displayTitle;

    const removeLineButton = orderRow.querySelector('[data-remove-row]');
    if (removeLineButton) {
      const removeLabel = this.dataset.removeLineLabel?.trim() || 'Remove';
      removeLineButton.setAttribute('aria-label', removeLabel);
      removeLineButton.setAttribute('title', removeLabel);
    }

    const quantityInput = orderRow.querySelector('[data-qty-input]');
    if (quantityInput) this.bindQuantityStepperSync(orderRow, quantityInput);

    this.syncTableUi();
    this.recalculateOrderGrandTotal();
  }

  /**
   * @param {HTMLTableRowElement} orderRow
   * @param {HTMLInputElement} quantityInput
   */
  bindQuantityStepperSync(orderRow, quantityInput) {
    const quantityHost = orderRow.querySelector('quantity-input');
    if (!quantityHost) return;

    const syncLineAndGrandTotal = () => {
      this.recalculateOrderRowLineTotal(quantityInput);
      this.recalculateOrderGrandTotal();
    };

    setTimeout(() => {
      quantityInput.addEventListener('change', syncLineAndGrandTotal);
      quantityInput.addEventListener('input', syncLineAndGrandTotal);
      quantityHost.querySelectorAll('[data-qty-btn]').forEach((stepperButton) => {
        stepperButton.addEventListener('click', () => {
          setTimeout(syncLineAndGrandTotal, 10);
        });
      });
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            syncLineAndGrandTotal();
            break;
          }
        }
      }).observe(quantityInput, { attributes: true, attributeFilter: ['value'] });
    }, 50);
  }

  /** @param {HTMLTableRowElement} orderRow */
  parseUnitPriceCentsFromOrderRow(orderRow) {
    const unitPriceCell = orderRow.querySelector('td:nth-child(2)');
    if (!unitPriceCell) return 0;
    const priceText = unitPriceCell.textContent.trim();
    const priceDollars = parseFloat(priceText.replace(/[^0-9.]/g, ''));
    return Number.isFinite(priceDollars) ? Math.round(priceDollars * 100) : 0;
  }

  /**
   * @returns {string}
   */
  resolveCartSectionId() {
    return (
      document.querySelector('#component-cart-drawer cart-form')?.dataset?.sectionId ||
      document.querySelector('cart-form')?.dataset?.sectionId ||
      'template-cart'
    );
  }

  /**
   * Points/Bonus only when shopper is a consultant or consultant ID is in localStorage.
   * @returns {boolean}
   */
  shouldAttachConsultantLineProperties() {
    return Utility.hasConsultantContextForLineProperties();
  }

  /**
   * Same line properties as PDP ATC.
   * HBB Associates: `Points`, `Bonus US` / `Bonus CA`.
   * HBB Customers: `_Points`, `_Bonus US` / `_Bonus CA`.
   * @param {HTMLTableRowElement} orderRow
   * @returns {Record<string, string> | null}
   */
  getConsultantLinePropertiesForOrderRow(orderRow) {
    return Utility.buildConsultantCartLineProperties(orderRow);
  }

  /** @param {Event} evt */
  async onBulkAddToCart(evt) {
    evt.preventDefault();

    if (Utility.requireCustomerLoginForCart()) return;

    const addToCartButton = evt.currentTarget;
    const tableBody = this.getOrderTableBody();
    if (!tableBody) return;

    const orderRows = Array.from(tableBody.querySelectorAll('tr[data-quick-order-row]'));
    if (orderRows.length === 0) return;

    const hasRestrictedRow = orderRows.some((orderRow) =>
      Utility.requireExclusiveCartProductCheck(orderRow.getAttribute('data-product-id'))
    );
    if (hasRestrictedRow) return;

    Utility.Animate.setLoading(addToCartButton, true);
    Utility.markAddToCartStart();

    const cartSectionId = this.resolveCartSectionId();
    const items = orderRows.map((orderRow) => {
      const quantityField = orderRow.querySelector('[data-qty-input]');
      const quantity = quantityField ? parseInt(quantityField.value, 10) || 1 : 1;
      /** @type {{ id: number|string, quantity: number, properties?: Record<string, string> }} */
      const item = {
        id: orderRow.getAttribute('data-variant-id'),
        quantity,
      };
      const properties = this.getConsultantLinePropertiesForOrderRow(orderRow);
      if (properties) item.properties = properties;
      return item;
    });

    try {
      const addResponse = await Utility.Fetch.json(
        `${window.routes.cart_add_url}.js`,
        Utility.Fetch.config('json', {
          body: JSON.stringify({
            items,
            sections: cartSectionId,
          }),
        })
      );

      Utility.Cart.rememberAddedItems(items);

      const cartForm =
        document.querySelector('#component-cart-drawer cart-form') ||
        document.querySelector('cart-form');
      const cartSectionHtml = addResponse.sections?.[cartSectionId];

      if (cartSectionHtml) {
        const parsedCartFragment = Utility.DOM.parse(cartSectionHtml);
        eventBus.publish(THEME_EVENTS.cartUpdate, {
          data: {
            action: 'add-item',
            item: addResponse,
            section: parsedCartFragment,
            elementToFocus: addToCartButton,
          },
        });
        if (!cartForm) {
          const cartJsonNode = Utility.DOM.extract(parsedCartFragment, '[data-cartScriptJSON]');
          if (cartJsonNode?.textContent && globalThis.themeData) {
            globalThis.themeData.cart = { ...JSON.parse(cartJsonNode.textContent) };
          }
          Utility.Cart.updateHeaderCount();
          Utility.handleSuccess('Items added to cart', 'Added to Cart');
        }
      } else {
        const fallbackApplied = this.hydrateCartFromAddResponseSections(addResponse);
        if (!fallbackApplied) {
          Utility.handleError(
            new Error('Cart section HTML was missing from the add response.'),
            'Add to Cart'
          );
          return;
        }
        const cartDrawer = document.querySelector('#component-cart-drawer');
        if (cartDrawer && typeof cartDrawer.openDrawer === 'function') {
          cartDrawer.openDrawer(addToCartButton);
        } else {
          window.location.href = globalThis.routes?.cart_fetch_url || '/cart';
        }
      }

      const itemCount = globalThis.themeData?.cart?.item_count;
      if (itemCount !== undefined) {
        Utility.announceToScreenReader(
          `Items added to cart. Cart now has ${itemCount} item${itemCount !== 1 ? 's' : ''}`,
          'assertive'
        );
      }

      orderRows.forEach((orderRow) => orderRow.remove());
      if (!tableBody.querySelector('tr[data-search-row]')) {
        this.appendSearchLine({ focus: true });
      }
      this.syncTableUi();
      this.recalculateOrderGrandTotal();
    } catch (error) {
      Utility.handleError(error, 'Add to Cart');
    } finally {
      Utility.Animate.setLoading(addToCartButton, false);
      Utility.markAddToCartEnd();
    }
  }

  syncTableUi() {
    const tableBody = this.getOrderTableBody();
    if (!tableBody) return;

    const tableHead = tableBody.closest('table')?.querySelector('thead');
    tableHead?.classList.remove('d-none');
    tableBody.querySelectorAll('tr[data-search-row]').forEach((searchRow) => {
      searchRow.classList.remove('d-none');
    });
  }

  /**
   * @param {HTMLTableRowElement|null} searchRow
   * @param {{ clearInput?: boolean }} [options]
   */
  clearSuggestUiForSearchRow(searchRow, options = {}) {
    if (!searchRow?.matches?.('[data-search-row]')) return;
    searchRow._qoSuggestAbort?.abort();
    const clearInput = options.clearInput !== false;
    const searchInput = searchRow.querySelector('[data-search-input]');
    const resultsContainer = searchRow.querySelector('[data-search-results]');
    const predictiveShell = searchRow.querySelector('[data-quick-order-predictive]');
    if (clearInput && searchInput) searchInput.value = '';
    if (resultsContainer) {
      resultsContainer.classList.add('d-none');
      resultsContainer.innerHTML = '';
      resultsContainer.scrollTop = 0;
    }
    const scrollRail = searchRow.querySelector('[data-search-scroll-rail]');
    if (scrollRail) scrollRail.hidden = true;
    predictiveShell?.classList.remove('show-results');
  }

  /** @param {{ clearInput?: boolean }} [options] */
  hideAllSuggestPanels(options = {}) {
    this.querySelectorAll('tr[data-search-row]').forEach((searchRow) => {
      this.clearSuggestUiForSearchRow(searchRow, options);
    });
  }

  /** @param {MouseEvent} evt */
  onComponentClick(evt) {
    if (evt.target.closest('a.predictive-search__view-all-btn')) return;

    const productCard = evt.target.closest('[data-product-card]');
    const searchRow = productCard?.closest('tr[data-search-row]');
    if (productCard && searchRow && this.contains(productCard)) {
      this.addOrderRowFromProductCard(productCard);
      return;
    }

    if (evt.target.closest('[data-remove-row]')) {
      const orderRow = evt.target.closest('tr[data-quick-order-row]');
      if (orderRow) {
        orderRow.remove();
        this.syncTableUi();
        this.recalculateOrderGrandTotal();
      }
    }
  }

  /** @param {Event} evt */
  onComponentChange(evt) {
    if (evt.target.matches('[data-qty-input]')) {
      this.recalculateOrderRowLineTotal(evt.target);
      this.recalculateOrderGrandTotal();
    }
  }

  /** @param {Event} evt */
  onComponentInput(evt) {
    if (evt.target.matches('[data-qty-input]')) {
      this.recalculateOrderRowLineTotal(evt.target);
      this.recalculateOrderGrandTotal();
    }
  }

  /** Quantity changes from inside `quantity-input` (document delegation). */
  onDocumentDelegatedQtyInput(evt) {
    if (!this.contains(evt.target)) return;
    if (!evt.target.matches('[data-qty-input]')) return;
    setTimeout(() => {
      this.recalculateOrderRowLineTotal(evt.target);
      this.recalculateOrderGrandTotal();
    }, 0);
  }

  /**
   * When `cart-form` is absent, apply section HTML from the cart add response.
   * @param {object} addResponse
   * @returns {boolean}
   */
  hydrateCartFromAddResponseSections(addResponse) {
    const cartSectionId = this.resolveCartSectionId();
    const sectionHtml = addResponse.sections?.[cartSectionId];
    if (!sectionHtml) return false;

    const parsedSection = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const cartJsonNode = parsedSection.querySelector('[data-cartScriptJSON]');
    if (cartJsonNode?.textContent && globalThis.themeData) {
      globalThis.themeData.cart = JSON.parse(cartJsonNode.textContent);
    }

    updateCartCountInHeader();
    Utility.handleSuccess('Items added to cart', 'Added to Cart');
    return true;
  }

  /** @param {HTMLInputElement} quantityInput */
  recalculateOrderRowLineTotal(quantityInput) {
    const orderRow = quantityInput.closest('tr[data-quick-order-row]');
    if (!orderRow) return;

    const quantity = parseInt(quantityInput.value, 10) || 1;
    const lineTotalElement = orderRow.querySelector('[data-row-total]');
    if (!lineTotalElement) return;

    const unitPriceCents = this.parseUnitPriceCentsFromOrderRow(orderRow);
    const lineTotalCents = unitPriceCents * quantity;

    lineTotalElement.textContent = Utility.formatMoneyText(
      lineTotalCents,
      globalThis.themeData?.money_format
    );
  }

  recalculateOrderGrandTotal() {
    const tableBody = this.getOrderTableBody();
    if (!tableBody) return;

    const orderRows = Array.from(tableBody.querySelectorAll('tr[data-quick-order-row]'));
    const grandTotalElement = this.getQuickOrderShell()?.querySelector('[data-total-price]');
    if (!grandTotalElement) return;

    let grandTotalCents = 0;
    orderRows.forEach((orderRow) => {
      const quantityField = orderRow.querySelector('[data-qty-input]');
      const quantity = quantityField ? parseInt(quantityField.value, 10) : 1;
      const unitPriceCents = this.parseUnitPriceCentsFromOrderRow(orderRow);
      grandTotalCents += unitPriceCents * quantity;
    });

    grandTotalElement.textContent = Utility.formatMoneyText(
      grandTotalCents,
      globalThis.themeData?.money_format
    );
  }
}

customElements.define('component-quick-order', ComponentQuickOrder);
